/**
 * DR_META XData — métadonnées structurées portées par les entités créées par DR.
 *
 * Objectif spike S1 : prouver qu'un XData **chaîne + multi-champs** survit dans
 * DrawingJS, en mémoire ET à travers un round-trip DWG (saveFile → openFile),
 * sur tous les types d'entités.
 *
 * Ce module généralise un pattern xData similaire à celui historiquement utilisé
 * par les marqueurs photo (un simple int16), avant que ces derniers ne s'appuient
 * uniquement sur l'attribut affiché (cf. `photomarker/creation.ts`).
 *
 * Disposition de la chaîne XData (positionnelle) :
 *   [1001] "DR_META"       nom d'application (RegApp)
 *   [1000] entityUid       string  — identité stable, clone-safe
 *   [1000] kind            string  — EntityType (nature CAD réelle : text/polyline/...)
 *   [1070] markerNumber    int16   — -1 si aucun
 *   [1000] createdAt       string  — epoch ms de création, "" si inconnu
 *   [1000] defectName      string  — nom du note_config actif au dessin, "" si aucun
 *   [1070] background      int16   — 1 si identité stampée à la volée (fond de plan,
 *                                    pas une création DR), 0 sinon. Absent = 0 (entités
 *                                    écrites avant ce champ, toujours de vraies créations).
 *   [1000] observationId   string  — `localId` (UUID) de la FieldObservation rattachée,
 *                                    "" si aucune. Le plan est la source de vérité de
 *                                    l'appartenance plan↔observation (clone-safe,
 *                                    survit au round-trip DWG) — la DB ne stocke PAS
 *                                    la liste des entités.
 *
 * `createdAt`/`defectName`/`background`/`observationId` sont en fin de chaîne (champs manquants →
 * valeur par défaut pour les entités écrites avant). `createdAt` donne l'ordre
 * chronologique du fil « Activité ». `defectName` = identité du désordre (le CALQUE
 * n'est PAS unique : plusieurs note_configs le partagent, ex. Fissure & Faïençage).
 * L'identité merge-safe reste `entityUid`.
 *
 * `kind` = NATURE de l'entité (réutilise `EntityType` de `entities/detection.ts`,
 * la même taxonomie que le reste du code — pas de classification dupliquée), orthogonal
 * à `background` = PROVENANCE. Une entité de fond de plan peut être Text, Polyline,
 * etc. — ce sont deux axes indépendants.
 *
 * NB : une string XData DWG est limitée à 255 octets par resbuf 1000. Nos champs
 * (UUID ~36 car.) sont bien en-deçà. Le spike teste aussi une string > 255 pour
 * lever ce point (chunking éventuel à prévoir si on stocke du JSON un jour).
 *
 * NB compat : `kind` était auparavant un `MetaRole` int16 (0-4). Les entités déjà
 * stampées avec l'ancien format sont détectées via `restype()` (int16 ≠ 1000) et
 * retombent sur `EntityType.Unknown` plutôt que de planter.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { EntityType } from "../entities/detection";

/** Nom d'application enregistré pour le XData DR_META. */
export const DR_META_APP_NAME = "DR_META";

// Codes DXF des resbuf utilisés dans la chaîne.
const XDATA_APP = 1001;
const XDATA_STRING = 1000;
const XDATA_INT16 = 1070;

/** Métadonnées DR portées par une entité. */
export interface EntityMeta {
  entityUid: string;
  /** Nature CAD réelle de l'entité (cf. `entities/detection.ts`). */
  kind: EntityType;
  markerNumber: number | null;
  /** Epoch ms de création (estampé au dessin). `null` = dessin antérieur au palier capture. */
  createdAt: number | null;
  /** Nom du note_config (désordre) actif au dessin. `null` = inconnu. */
  defectName: string | null;
  /**
   * `true` si l'identité a été stampée à la volée sur une entité existante (fond de
   * plan touché par une suppression/modification), PAS au moment d'une création DR.
   * Sert à exclure ces entités du fil « live » (sinon un undo après suppression les
   * ferait apparaître comme de vrais désordres DR, alors qu'elles n'ont jamais été
   * dessinées par l'inspecteur).
   */
  background: boolean;
  /**
   * `localId` (UUID) de la FieldObservation à laquelle l'entité est rattachée.
   * `null` = aucune. Source de vérité de l'appartenance plan↔observation.
   */
  observationId: string | null;
}

/**
 * Enregistre l'application DR_META dans la table RegApp de la base.
 * Idempotent — sans effet si déjà enregistrée.
 */
export function registerMetaApp(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): void {
  const appStr = new module.OdString(DR_META_APP_NAME);
  db.newRegApp(appStr);
  deleteObj(appStr);
}

/**
 * Écrit (ou remplace) le XData DR_META sur une entité.
 * L'entité est ouverte en écriture le temps de l'opération.
 */
export function writeEntityMeta(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  meta: EntityMeta,
): void {
  const entity = entityId.safeOpenObject(
    OpenMode.kForWrite,
    false,
  ) as DrawingModule.OdDbObject;

  // Toutes les allocations sont trackées et libérées dans le finally : si l'une
  // d'elles lève (RegApp, resbuf, setXData...), l'entité ne doit jamais rester
  // verrouillée en écriture — sinon le highlight/redraw suivant plante en
  // "null function" WASM (même pattern que le fix des leader lines).
  let appStr: DrawingModule.OdString | null = null;
  let head: DrawingModule.OdResBuf | null = null;
  let uidStr: DrawingModule.OdString | null = null;
  let uidRb: DrawingModule.OdResBuf | null = null;
  let kindStr: DrawingModule.OdString | null = null;
  let kindRb: DrawingModule.OdResBuf | null = null;
  let markerRb: DrawingModule.OdResBuf | null = null;
  let createdStr: DrawingModule.OdString | null = null;
  let createdRb: DrawingModule.OdResBuf | null = null;
  let defectStr: DrawingModule.OdString | null = null;
  let defectRb: DrawingModule.OdResBuf | null = null;
  let backgroundRb: DrawingModule.OdResBuf | null = null;
  let observationStr: DrawingModule.OdString | null = null;
  let observationRb: DrawingModule.OdResBuf | null = null;

  try {
    // L'app doit être enregistrée dans la base avant d'attacher le XData.
    const db = entity.database();
    registerMetaApp(module, db);

    // Construction de la chaîne resbuf.
    appStr = new module.OdString(DR_META_APP_NAME);
    head = module.OdResBuf.newRb(XDATA_APP);
    head.setString(appStr);

    uidStr = new module.OdString(meta.entityUid);
    uidRb = module.OdResBuf.newRb(XDATA_STRING);
    uidRb.setString(uidStr);

    kindStr = new module.OdString(meta.kind);
    kindRb = module.OdResBuf.newRb(XDATA_STRING);
    kindRb.setString(kindStr);

    markerRb = module.OdResBuf.newRb(XDATA_INT16);
    markerRb.setInt16(meta.markerNumber ?? -1);

    createdStr = new module.OdString(
      meta.createdAt != null ? String(meta.createdAt) : "",
    );
    createdRb = module.OdResBuf.newRb(XDATA_STRING);
    createdRb.setString(createdStr);

    defectStr = new module.OdString(meta.defectName ?? "");
    defectRb = module.OdResBuf.newRb(XDATA_STRING);
    defectRb.setString(defectStr);

    backgroundRb = module.OdResBuf.newRb(XDATA_INT16);
    backgroundRb.setInt16(meta.background ? 1 : 0);

    observationStr = new module.OdString(meta.observationId ?? "");
    observationRb = module.OdResBuf.newRb(XDATA_STRING);
    observationRb.setString(observationStr);

    head.setNext(uidRb);
    uidRb.setNext(kindRb);
    kindRb.setNext(markerRb);
    markerRb.setNext(createdRb);
    createdRb.setNext(defectRb);
    defectRb.setNext(backgroundRb);
    backgroundRb.setNext(observationRb);

    entity.setXData(head);
  } finally {
    // Nettoyage (enfants d'abord, puis têtes/strings) — toujours exécuté.
    if (observationRb) deleteObj(observationRb);
    if (defectRb) deleteObj(defectRb);
    if (createdRb) deleteObj(createdRb);
    if (markerRb) deleteObj(markerRb);
    if (backgroundRb) deleteObj(backgroundRb);
    if (kindRb) deleteObj(kindRb);
    if (uidRb) deleteObj(uidRb);
    if (head) deleteObj(head);
    if (observationStr) deleteObj(observationStr);
    if (defectStr) deleteObj(defectStr);
    if (createdStr) deleteObj(createdStr);
    if (kindStr) deleteObj(kindStr);
    if (uidStr) deleteObj(uidStr);
    if (appStr) deleteObj(appStr);
    deleteObj(entity);
  }
}

/**
 * Relit le XData DR_META d'une entité, ou `null` si absent.
 */
export function readEntityMeta(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): EntityMeta | null {
  const entity = entityId.safeOpenObject(OpenMode.kForRead, false);
  const appStr = new module.OdString(DR_META_APP_NAME);
  let head: DrawingModule.OdResBuf | null = null;
  try {
    head = entity.xData1(appStr);
  } finally {
    deleteObj(appStr);
    deleteObj(entity);
  }

  if (!head) return null;

  // Récupère toute la chaîne comme wrappers distincts avant lecture.
  // (Chaîne : [1001] app → [1000] uid → [1000] kind → [1070] marker → [1000] createdAt
  //  → [1000] defectName → [1070] background → [1000] observationId)
  // Borne de sécurité : le type déclare next(): OdResBuf (non-nullable) mais
  // renvoie en réalité une valeur falsy en fin de chaîne (cf. photomarker/detection.ts).
  // On plafonne pour ne jamais boucler indéfiniment.
  const MAX_NODES = 9;
  const nodes: DrawingModule.OdResBuf[] = [];
  let cur: DrawingModule.OdResBuf | null = head;
  while (cur && nodes.length < MAX_NODES) {
    nodes.push(cur);
    cur = cur.next();
  }

  let result: EntityMeta | null = null;

  // On attend au moins app + uid + kind + marker = 4 nœuds.
  // createdAt (nœud 5) / defectName (nœud 6) / background (nœud 7) sont optionnels.
  if (nodes.length >= 4) {
    const uidOd = nodes[1].getString();
    const entityUid = uidOd.toString();
    deleteObj(uidOd);

    // `kind` était un int16 (ancien MetaRole) avant ce champ — restype() distingue
    // l'ancien format du nouveau plutôt que de planter sur un getString() erroné.
    let kind: EntityType = EntityType.Unknown;
    if (nodes[2].restype() === XDATA_STRING) {
      const kindOd = nodes[2].getString();
      const kindRaw = kindOd.toString();
      deleteObj(kindOd);
      if ((Object.values(EntityType) as string[]).includes(kindRaw)) {
        kind = kindRaw as EntityType;
      }
    }

    const markerRaw = nodes[3].getInt16();

    let createdAt: number | null = null;
    if (nodes.length >= 5) {
      const createdOd = nodes[4].getString();
      const createdRaw = createdOd.toString();
      deleteObj(createdOd);
      if (createdRaw !== "") {
        const parsed = Number(createdRaw);
        createdAt = Number.isNaN(parsed) ? null : parsed;
      }
    }

    let defectName: string | null = null;
    if (nodes.length >= 6) {
      const defectOd = nodes[5].getString();
      const defectRaw = defectOd.toString();
      deleteObj(defectOd);
      defectName = defectRaw === "" ? null : defectRaw;
    }

    // Absent (entité écrite avant ce champ) = false : c'était forcément une vraie
    // création DR, ce champ n'existant pas encore avant le stamp à la volée.
    const background = nodes.length >= 7 ? nodes[6].getInt16() === 1 : false;

    // Absent (entité écrite avant ce champ) = null : pas de rattachement.
    let observationId: string | null = null;
    if (nodes.length >= 8 && nodes[7].restype() === XDATA_STRING) {
      const observationOd = nodes[7].getString();
      const observationRaw = observationOd.toString();
      deleteObj(observationOd);
      observationId = observationRaw === "" ? null : observationRaw;
    }

    result = {
      entityUid,
      kind,
      markerNumber: markerRaw < 0 ? null : markerRaw,
      createdAt,
      defectName,
      background,
      observationId,
    };
  }

  // Nettoyage des wrappers (enfants → tête).
  for (let i = nodes.length - 1; i >= 0; i--) {
    deleteObj(nodes[i]);
  }

  return result;
}
